import React, { createContext, useContext, useState, useEffect } from 'react';

// Currency configuration
// Initial currency configuration - rates will be updated from API
const CURRENCIES = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    flag: 'US',
    locale: 'en-US',
    rate: 1
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    name: 'Brazilian Real',
    flag: 'BR',
    locale: 'pt-BR',
    rate: 5.10 // Default fallback rate, will be updated from API
  }
};

// Real-time exchange rate service
const ExchangeRateService = {
  // API endpoints for exchange rates (using free tier)
  endpoints: [
    'https://api.exchangerate-api.com/v4/latest/USD',
    'https://api.fixer.io/latest?base=USD&access_key=YOUR_API_KEY', // Fallback
  ],
  
  updateExchangeRates: async () => {
    try {
      // Try primary endpoint first
      const response = await fetch(CURRENCIES.USD.rate === 1 ? 
        'https://api.exchangerate-api.com/v4/latest/USD' : 
        'https://api.exchangerate-api.com/v4/latest/USD'
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Update rates for all currencies except USD (base currency)
      if (data.rates && data.rates.BRL) {
        CURRENCIES.BRL.rate = Number(data.rates.BRL.toFixed(4));
        
        // Store updated rates in localStorage with timestamp
        const rateData = {
          rates: { BRL: CURRENCIES.BRL.rate },
          lastUpdated: new Date().toISOString()
        };
        localStorage.setItem('exchangeRates', JSON.stringify(rateData));
        
        return true;
      }
    } catch (error) {
      // Fallback to cached rates if available
      try {
        const cachedRates = localStorage.getItem('exchangeRates');
        if (cachedRates) {
          const { rates, lastUpdated } = JSON.parse(cachedRates);
          const cacheAge = (new Date() - new Date(lastUpdated)) / (1000 * 60 * 60); // hours
          
          // Use cached rates if less than 24 hours old
          if (cacheAge < 24 && rates.BRL) {
            CURRENCIES.BRL.rate = rates.BRL;
            return true;
          }
        }
      } catch (cacheError) {
        // Silent fallback to default rates
      }
      
      return false;
    }
  },
  
  // Initialize exchange rates on startup
  initialize: async () => {
    // Load cached rates first for immediate use
    try {
      const cachedRates = localStorage.getItem('exchangeRates');
      if (cachedRates) {
        const { rates } = JSON.parse(cachedRates);
        if (rates.BRL) {
          CURRENCIES.BRL.rate = rates.BRL;
        }
      }
    } catch (error) {
      // Silent fallback to default rates
    }
    
    // Update with fresh rates in background
    await ExchangeRateService.updateExchangeRates();
  }
};

// Create context
const CurrencyContext = createContext();

// Hook to use currency context
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Currency provider component
export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    // Get currency from localStorage or default to USD
    const savedCurrency = localStorage.getItem('preferredCurrency');
    return savedCurrency && CURRENCIES[savedCurrency] ? savedCurrency : 'USD';
  });

  const [ratesLoaded, setRatesLoaded] = useState(false);

  // Initialize exchange rates on component mount
  useEffect(() => {
    const initializeRates = async () => {
      await ExchangeRateService.initialize();
      setRatesLoaded(true);
    };
    
    initializeRates();
    
    // Set up periodic updates (every 4 hours)
    const interval = setInterval(async () => {
      await ExchangeRateService.updateExchangeRates();
    }, 4 * 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Save currency preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('preferredCurrency', currency);
  }, [currency]);

  // Convert amount from USD to selected currency with validation
  const convertPrice = (usdAmount) => {
    // Input validation
    if (usdAmount === null || usdAmount === undefined) {
      return 0;
    }
    
    const numAmount = Number(usdAmount);
    if (isNaN(numAmount)) {
      console.warn('Invalid amount provided to convertPrice:', usdAmount);
      return 0;
    }
    
    const rate = CURRENCIES[currency]?.rate || 1;
    return Math.round(numAmount * rate * 100) / 100;
  };

  // Create cached formatters for performance
  const formattersRef = React.useRef({});
  
  // Format price in selected currency with caching
  const formatPrice = (usdAmount, options = {}) => {
    const converted = convertPrice(usdAmount);
    const currencyData = CURRENCIES[currency];
    
    if (!currencyData) {
      console.warn('Invalid currency:', currency);
      return '$0'; // Fallback to USD
    }
    
    // Create cache key for formatter
    const cacheKey = `${currencyData.code}_${JSON.stringify(options)}`;
    
    // Get or create cached formatter
    if (!formattersRef.current[cacheKey]) {
      formattersRef.current[cacheKey] = new Intl.NumberFormat(currencyData.locale, {
        style: 'currency',
        currency: currencyData.code,
        minimumFractionDigits: options.minimumFractionDigits ?? 0,
        maximumFractionDigits: options.maximumFractionDigits ?? 0,
        ...options
      });
    }
    
    return formattersRef.current[cacheKey].format(converted);
  };

  // Format price range
  const formatPriceRange = (minUsd, maxUsd, options = {}) => {
    if (maxUsd) {
      return `${formatPrice(minUsd, options)} - ${formatPrice(maxUsd, options)}`;
    }
    return formatPrice(minUsd, options);
  };

  // Get current currency info
  const getCurrencyInfo = () => CURRENCIES[currency];

  const value = {
    currency,
    setCurrency,
    convertPrice,
    formatPrice,
    formatPriceRange,
    getCurrencyInfo,
    currencies: CURRENCIES,
    availableCurrencies: Object.keys(CURRENCIES),
    ratesLoaded,
    // Expose exchange rate service for manual updates if needed
    updateExchangeRates: ExchangeRateService.updateExchangeRates
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;